import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import {
  BookOpen,
  CalendarDays,
  ChevronDown,
  CreditCard,
  TicketPercent,
  GraduationCap,
  Layers,
  MessageCircle,
  Search,
  ShieldCheck,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@hey404/ui";
import { apiFetch } from "./lib/auth";
import { btnSecondary } from "./admin-ui";
import { AdminSkeleton, LoadingBlock } from "./loading";

/**
 * Auditoría: the admin activity log as a timeline. Each entry reads as a
 * sentence ("Andrea Molina suspendió a Camila Ruiz"), grouped by day, with
 * an area tag and icon. Filters (area, search, year and month) run on the
 * API so they cover the whole history; pages load as you scroll. Raw action
 * codes only show for actions this file doesn't know.
 */

type AuditLog = {
  id: string;
  actorEmail: string;
  actorName: string | null;
  action: string;
  targetType: string;
  targetId: string;
  targetLabel: string | null;
  details: string;
  createdAt: string;
};

type Area =
  "content" | "people" | "moderation" | "commerce" | "coupons" | "academy";

const areas: Record<Area, { label: string; icon: LucideIcon; tone: string }> = {
  content: { label: "Cursos", icon: BookOpen, tone: "bg-acid" },
  people: { label: "Personas", icon: Users, tone: "bg-grape" },
  moderation: { label: "Moderación", icon: MessageCircle, tone: "bg-bubble" },
  commerce: { label: "Pagos", icon: CreditCard, tone: "bg-lemon" },
  coupons: { label: "Cupones", icon: TicketPercent, tone: "bg-bone" },
  academy: { label: "Academia", icon: GraduationCap, tone: "bg-paper" },
};

// Verb phrase (it reads after the actor's name) and area per action.
const actions: Record<string, { verb: string; area: Area }> = {
  "course.created": { verb: "creó el curso", area: "content" },
  "course.updated": { verb: "editó el curso", area: "content" },
  "course.deleted": { verb: "eliminó el curso", area: "content" },
  "course.duplicated": { verb: "duplicó el curso", area: "content" },
  "course.imported": { verb: "importó el curso", area: "content" },
  "module.duplicated": { verb: "duplicó el módulo", area: "content" },
  "lesson.moved": { verb: "movió la clase", area: "content" },
  "video.upload_started": { verb: "subió un video a", area: "content" },
  "waitlist.notified": {
    verb: "avisó a la lista de espera de",
    area: "content",
  },
  "enrollment.created": { verb: "inscribió a una persona en", area: "people" },
  "enrollment.removed": { verb: "quitó una inscripción de", area: "people" },
  "user.reset_link_generated": {
    verb: "generó un link de acceso para",
    area: "people",
  },
  "user.banned": { verb: "suspendió a", area: "people" },
  "user.unbanned": { verb: "reactivó a", area: "people" },
  "user.deleted": { verb: "eliminó la cuenta de", area: "people" },
  "administrator.invited": {
    verb: "invitó como administrador a",
    area: "people",
  },
  "administrator.updated": { verb: "cambió el rol de", area: "people" },
  "administrator.removed": {
    verb: "quitó el acceso administrativo de",
    area: "people",
  },
  "comment.hidden": { verb: "ocultó un comentario de", area: "moderation" },
  "comment.restored": {
    verb: "restauró un comentario de",
    area: "moderation",
  },
  "comment.replied": {
    verb: "respondió un comentario de",
    area: "moderation",
  },
  "forum_post.approved": {
    verb: "aprobó un post del foro de",
    area: "moderation",
  },
  "forum_post.rejected": {
    verb: "rechazó un post del foro de",
    area: "moderation",
  },
  "subscription.granted": {
    verb: "asignó una suscripción de cortesía a",
    area: "commerce",
  },
  "discount_code.issued": {
    verb: "emitió códigos de descuento",
    area: "commerce",
  },
  "coupon.created": { verb: "creó la campaña", area: "coupons" },
  "coupon.updated": { verb: "editó la campaña", area: "coupons" },
  "coupon.paused": { verb: "pausó la campaña", area: "coupons" },
  "coupon.resumed": { verb: "reactivó la campaña", area: "coupons" },
  "coupon.archived": { verb: "archivó la campaña", area: "coupons" },
  "coupon.codes_generated": {
    verb: "generó códigos en la campaña",
    area: "coupons",
  },
  "coupon.code_disabled": {
    verb: "desactivó un código de",
    area: "coupons",
  },
  "coupon.code_enabled": { verb: "activó un código de", area: "coupons" },
  "coupon.exported": { verb: "exportó la campaña", area: "coupons" },
};

const roleLabels: Record<string, string> = {
  owner: "Propietario",
  administrator: "Administrador",
  editor: "Editor",
  content_manager: "Gestor de contenido",
  instructor: "Profesor",
  moderator: "Moderador",
  support: "Soporte",
};

// Academia writes one generic entry per change: method + request path.
const academyVerbs: Record<string, string> = {
  post: "creó",
  patch: "editó",
  put: "actualizó",
  delete: "eliminó",
};
const academyTargets: Record<string, string> = {
  schools: "una escuela",
  paths: "una ruta",
  events: "un evento en vivo",
  teams: "un equipo",
  submissions: "una entrega de proyecto",
  instructors: "un profesor",
  invitations: "una invitación de equipo",
  members: "un miembro de equipo",
};

function describe(log: AuditLog): {
  area: Area | null;
  verb: string;
  target: string;
  extra: string;
} {
  if (log.action.startsWith("learning_tools.")) {
    const method = log.action.split(".")[1] ?? "";
    const segments = log.targetId.split("/").filter(Boolean);
    const resource =
      [...segments].reverse().find((part) => academyTargets[part]) ?? "";
    return {
      area: "academy",
      verb: `${academyVerbs[method] ?? "cambió"} ${academyTargets[resource] ?? "contenido"} en Academia`,
      target: "",
      extra: "",
    };
  }
  const known = actions[log.action];
  const details = log.details?.trim() ?? "";
  // Details carry a readable fallback for some actions (email, title, role).
  const looksReadable =
    details && !details.startsWith("{") && details.length < 80;
  let target = log.targetLabel ?? "";
  let extra = "";
  if (log.action === "subscription.granted") {
    const [email, interval] = details.split(" · ");
    target ||= email ?? "";
    extra =
      interval === "annual" ? "Plan anual" : interval ? "Plan mensual" : "";
  } else if (log.action.startsWith("coupon.")) {
    // Details are "<extra> · <campaign name>" or just the name (see
    // admin-coupons.ts); the target label already carries the name.
    const [first, ...rest] = details.split(" · ");
    target ||= rest.length ? rest.join(" · ") : (first ?? "");
    if (log.action === "coupon.codes_generated") extra = `${first} códigos`;
    else if (log.action === "coupon.exported")
      extra = first === "codes" ? "Códigos" : "Usos";
    else if (log.action.startsWith("coupon.code_")) extra = first ?? "";
  } else if (log.action.startsWith("administrator.")) {
    extra = roleLabels[details] ?? "";
  } else if (log.action === "enrollment.created") {
    extra = details;
  } else if (!target && looksReadable) {
    target = details;
  } else if (looksReadable && details !== target && log.targetType === "user") {
    extra = details;
  }
  return {
    area: known?.area ?? null,
    verb: known?.verb ?? log.action,
    target: target || (known ? "un elemento eliminado" : log.targetId),
    extra,
  };
}

const dayKey = (iso: string) => new Date(iso).toDateString();
function dayLabel(iso: string) {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);
  if (date.toDateString() === today.toDateString()) return "Hoy";
  if (date.toDateString() === yesterday.toDateString()) return "Ayer";
  // Past years say which one: the log reaches back further than a year.
  const label = date.toLocaleDateString("es", {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(date.getFullYear() !== today.getFullYear() && { year: "numeric" }),
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

const PAGE = 40;

const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

/** Debounced copy of a value, so typing in the search box is one request. */
function useDebounced<T>(value: T, ms: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function AuditAdmin() {
  const [area, setArea] = useState<Area | "">("");
  const [query, setQuery] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const search = useDebounced(query.trim(), 300);
  // The chosen month or year as an instant range in the admin's timezone,
  // so "octubre" means their October, not UTC's.
  const range = (() => {
    if (!year) return null;
    const y = Number(year);
    const m = month ? Number(month) - 1 : null;
    const from = new Date(y, m ?? 0, 1);
    const to = m === null ? new Date(y + 1, 0, 1) : new Date(y, m + 1, 1);
    return { from: from.toISOString(), to: to.toISOString() };
  })();
  const filters = { area, search, from: range?.from, to: range?.to };
  const feed = useInfiniteQuery({
    queryKey: ["admin", "audit", filters],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE) });
      if (pageParam) params.set("before", pageParam);
      if (area) params.set("area", area);
      if (search) params.set("q", search);
      if (range) {
        params.set("from", range.from);
        params.set("to", range.to);
      }
      return apiFetch<{
        logs: AuditLog[];
        nextCursor: string | null;
        years: number[];
      }>(`/api/admin/audit?${params}`);
    },
    getNextPageParam: (page) => page.nextCursor,
    placeholderData: keepPreviousData,
  });
  // Infinite scroll: a sentinel under the list loads the next page as it
  // nears the viewport (with a margin, so it is usually ready on arrival).
  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = feed;
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage)
          void fetchNextPage();
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (feed.isPending) return <AdminSkeleton variant="audit" />;
  if (feed.isError || !feed.data)
    return (
      <p role="alert" className="text-sm">
        No pudimos cargar la auditoría. Recarga la página para intentar de
        nuevo.
      </p>
    );

  const logs = feed.data.pages.flatMap((page) => page.logs);
  const years = feed.data.pages[0]?.years ?? [];
  const filtering = Boolean(area || search || year);
  const filtered = logs.map((log) => ({ log, ...describe(log) }));
  // The same person repeating the same action on the same target, back to
  // back (five access links in a row), reads as one entry with a count.
  const collapsed: Array<(typeof filtered)[number] & { times: number }> = [];
  for (const entry of filtered) {
    const last = collapsed[collapsed.length - 1];
    if (
      last &&
      last.log.actorEmail === entry.log.actorEmail &&
      last.log.action === entry.log.action &&
      last.target === entry.target &&
      dayKey(last.log.createdAt) === dayKey(entry.log.createdAt)
    )
      last.times += 1;
    else collapsed.push({ ...entry, times: 1 });
  }
  const visible = collapsed;
  const days: Array<{ key: string; label: string; items: typeof visible }> = [];
  for (const entry of visible) {
    const key = dayKey(entry.log.createdAt);
    const last = days[days.length - 1];
    if (last?.key === key) last.items.push(entry);
    else
      days.push({ key, label: dayLabel(entry.log.createdAt), items: [entry] });
  }
  const periodLabel = year
    ? month
      ? `${MONTHS[Number(month) - 1]} ${year}`
      : year
    : null;

  return (
    <section>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-extrabold">Actividad</h2>
          <span
            title={hasNextPage ? "Hay más: se cargan al bajar" : undefined}
            className="border-ink bg-lemon border-chip rotate-[-2deg] px-2 py-0.5 font-mono text-xs font-bold"
          >
            {logs.length}
            {hasNextPage ? "+" : ""}
          </span>
          {feed.isFetching && !isFetchingNextPage && (
            <span className="text-ink-soft font-mono text-xs">Buscando…</span>
          )}
        </div>
      </div>
      <FilterBar
        query={query}
        onQuery={setQuery}
        area={area}
        onArea={setArea}
        year={year}
        month={month}
        years={years}
        onPeriod={(nextYear, nextMonth) => {
          setYear(nextYear);
          setMonth(nextMonth);
        }}
        onClear={() => {
          setArea("");
          setQuery("");
          setYear("");
          setMonth("");
        }}
      />

      {filtered.length === 0 ? (
        <div className="border-ink/30 bg-bone/60 border-chip mt-6 flex items-center gap-4 border-dashed p-6">
          <span className="border-ink bg-paper border-chip grid h-11 w-11 shrink-0 rotate-[-4deg] place-items-center">
            <ShieldCheck size={20} aria-hidden="true" />
          </span>
          <div>
            <p className="font-display text-lg font-extrabold">
              {filtering ? "Sin resultados" : "Todavía no hay actividad"}
            </p>
            <p className="text-ink-soft text-sm">
              {filtering
                ? "Nada coincide con estos filtros. Prueba con otro período, área o búsqueda."
                : "Aquí verás cada cambio sensible que haga el equipo."}
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          {days.map((day) => (
            <div key={day.key}>
              <h3 className="text-ink-soft font-mono text-xs font-bold uppercase tracking-[0.08em]">
                {day.label}
              </h3>
              <ol className="border-ink bg-bone border-chip divide-ink/15 mt-3 divide-y">
                {day.items.map(
                  ({ log, area: entryArea, verb, target, extra, times }) => {
                    const meta = entryArea ? areas[entryArea] : null;
                    const Icon = meta?.icon ?? ShieldCheck;
                    return (
                      <li
                        key={log.id}
                        className="flex items-start gap-4 px-5 py-4"
                      >
                        <span
                          aria-hidden="true"
                          className={cn(
                            "border-ink border-chip grid h-9 w-9 shrink-0 place-items-center",
                            meta?.tone ?? "bg-paper",
                          )}
                        >
                          <Icon size={16} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm leading-relaxed">
                            <strong
                              className="person-name"
                              title={log.actorEmail}
                            >
                              {log.actorName ?? log.actorEmail}
                            </strong>{" "}
                            <span
                              className={meta ? undefined : "font-mono text-xs"}
                            >
                              {verb}
                            </span>
                            {target && (
                              <>
                                {" "}
                                <strong className="person-name">
                                  {target}
                                </strong>
                              </>
                            )}
                          </p>
                          <p className="text-ink-soft mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-xs">
                            {meta && (
                              <span className="border-ink/40 border px-1.5 py-0.5 font-bold uppercase leading-none">
                                {meta.label}
                              </span>
                            )}
                            {extra && <span>{extra}</span>}
                            {times > 1 && (
                              <span className="font-bold">{times} veces</span>
                            )}
                          </p>
                        </div>
                        <time
                          dateTime={log.createdAt}
                          title={new Date(log.createdAt).toLocaleString(
                            "es-AR",
                          )}
                          className="text-ink-soft shrink-0 font-mono text-xs"
                        >
                          {new Date(log.createdAt).toLocaleTimeString("es", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </time>
                      </li>
                    );
                  },
                )}
              </ol>
            </div>
          ))}
          <div ref={sentinel} aria-live="polite">
            {isFetchingNextPage ? (
              <div className="border-ink bg-bone border-chip divide-ink/15 divide-y">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="flex items-center gap-4 px-5 py-4">
                    <LoadingBlock className="h-9 w-9 shrink-0" />
                    <LoadingBlock className="h-4 w-2/3" />
                  </div>
                ))}
                <span className="sr-only">Cargando más actividad…</span>
              </div>
            ) : hasNextPage ? (
              // Shown only if the observer hasn't fired (e.g. very tall screen).
              <MoreButton onClick={() => void fetchNextPage()}>
                Cargar más actividad
              </MoreButton>
            ) : (
              <p className="text-ink-soft text-center font-mono text-xs">
                {periodLabel
                  ? `Eso es todo en ${periodLabel}.`
                  : "Eso es todo: llegaste al primer registro."}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ── Filter bar ───────────────────────────────────────────────────────────────
// One bar for the whole query: search on the left, the Área and Fecha
// filters as compact buttons inside it on the right. A set filter shows its
// value on its button (with an × to drop just that one); the date panel picks
// a year, then optionally a month on a 4×3 grid.

function FilterMenu({
  label,
  value,
  onClear,
  icon: Icon,
  children,
}: {
  label: string;
  /** The active value; the button shows it instead of the label. */
  value: string | null;
  onClear: () => void;
  icon: LucideIcon;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelId = useId();
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <div
        className={cn(
          "rounded-control flex h-10 items-stretch border transition-colors",
          value
            ? "border-ink bg-lemon"
            : "hover:bg-paper-dim border-transparent",
        )}
      >
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen((current) => !current)}
          className="flex cursor-pointer items-center gap-2 whitespace-nowrap pl-3 pr-2.5 text-sm"
        >
          <Icon size={15} aria-hidden="true" />
          <span className={value ? "font-bold" : undefined}>
            {value ?? label}
          </span>
          {!value && (
            <ChevronDown
              size={14}
              aria-hidden="true"
              className={cn("transition-transform", open && "rotate-180")}
            />
          )}
        </button>
        {value && (
          <button
            type="button"
            onClick={onClear}
            aria-label={`Quitar filtro de ${label.toLowerCase()}`}
            title="Quitar filtro"
            className="border-ink/30 hover:bg-ink hover:text-paper grid w-8 cursor-pointer place-items-center border-l transition-colors"
          >
            <X size={13} aria-hidden="true" />
          </button>
        )}
      </div>
      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label={label}
          className="border-ink bg-paper border-chip rounded-control shadow-brutal absolute right-0 top-full z-30 mt-2 w-72 p-3"
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

const panelOption = (active: boolean) =>
  cn(
    "cursor-pointer rounded-[4px] px-2.5 py-2 text-sm transition-colors",
    active ? "bg-ink text-paper font-bold" : "hover:bg-lemon/45",
  );

function FilterBar({
  query,
  onQuery,
  area,
  onArea,
  year,
  month,
  years,
  onPeriod,
  onClear,
}: {
  query: string;
  onQuery: (value: string) => void;
  area: Area | "";
  onArea: (value: Area | "") => void;
  year: string;
  month: string;
  years: number[];
  onPeriod: (year: string, month: string) => void;
  onClear: () => void;
}) {
  // The year the date panel is browsing (before a month is picked).
  const [draftYear, setDraftYear] = useState(
    year || String(years[0] ?? new Date().getFullYear()),
  );
  const period = year
    ? month
      ? `${MONTHS[Number(month) - 1]} ${year}`
      : year
    : null;
  const any = Boolean(query.trim() || area || year);
  return (
    <div className="border-ink bg-paper border-chip rounded-control mt-5 flex flex-wrap items-center gap-1 p-1.5 focus-within:shadow-[0_0_0_3px_var(--focus-ring)]">
      <label className="flex min-w-[min(100%,16rem)] flex-1 items-center gap-2.5 px-2.5">
        <Search
          size={16}
          aria-hidden="true"
          className="text-ink-soft shrink-0"
        />
        <input
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          aria-label="Buscar en la actividad"
          placeholder="Buscar por persona, curso o detalle"
          className="bar-input placeholder:text-ink-soft h-10 min-w-0 flex-1 bg-transparent text-base"
        />
        {query && (
          <button
            type="button"
            onClick={() => onQuery("")}
            aria-label="Borrar búsqueda"
            className="text-ink-soft hover:text-ink grid h-7 w-7 cursor-pointer place-items-center"
          >
            <X size={14} aria-hidden="true" />
          </button>
        )}
      </label>
      <span aria-hidden="true" className="bg-ink/15 hidden h-6 w-px sm:block" />
      <FilterMenu
        label="Área"
        icon={Layers}
        value={area ? areas[area].label : null}
        onClear={() => onArea("")}
      >
        {(close) => (
          <div className="grid gap-0.5">
            {(Object.keys(areas) as Area[]).map((key) => {
              const Icon = areas[key].icon;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    onArea(area === key ? "" : key);
                    close();
                  }}
                  className={cn(
                    panelOption(area === key),
                    "flex items-center gap-2.5 text-left",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "border-ink border-chip grid h-6 w-6 shrink-0 place-items-center",
                      areas[key].tone,
                      area === key && "text-ink",
                    )}
                  >
                    <Icon size={12} />
                  </span>
                  {areas[key].label}
                </button>
              );
            })}
          </div>
        )}
      </FilterMenu>
      <FilterMenu
        label="Fecha"
        icon={CalendarDays}
        value={period}
        onClear={() => onPeriod("", "")}
      >
        {(close) => (
          <div>
            <p className="text-ink-soft font-mono text-[11px] font-bold uppercase tracking-[0.06em]">
              Año
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {(years.length ? years : [new Date().getFullYear()]).map(
                (item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setDraftYear(String(item))}
                    className={cn(
                      panelOption(draftYear === String(item)),
                      "border-ink/20 border font-mono",
                    )}
                  >
                    {item}
                  </button>
                ),
              )}
            </div>
            <p className="text-ink-soft mt-3 font-mono text-[11px] font-bold uppercase tracking-[0.06em]">
              Mes
            </p>
            <div className="mt-1.5 grid grid-cols-4 gap-1">
              {MONTHS.map((name, index) => {
                const value = String(index + 1);
                const active = year === draftYear && month === value;
                // Months that haven't happened yet can't have activity.
                const now = new Date();
                const future =
                  Number(draftYear) > now.getFullYear() ||
                  (Number(draftYear) === now.getFullYear() &&
                    index > now.getMonth());
                return (
                  <button
                    key={name}
                    type="button"
                    disabled={future}
                    onClick={() => {
                      onPeriod(draftYear, value);
                      close();
                    }}
                    className={cn(
                      panelOption(active),
                      "text-center disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent",
                    )}
                  >
                    {name.slice(0, 3)}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => {
                onPeriod(draftYear, "");
                close();
              }}
              className={cn(
                panelOption(year === draftYear && !month),
                "border-ink/20 mt-2 w-full border",
              )}
            >
              Todo {draftYear}
            </button>
          </div>
        )}
      </FilterMenu>
      {any && (
        <button
          type="button"
          onClick={onClear}
          className="text-ink-soft hover:text-ink font-display cursor-pointer whitespace-nowrap px-3 text-sm font-bold underline-offset-4 hover:underline"
        >
          Limpiar
        </button>
      )}
    </div>
  );
}

const MoreButton = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) => (
  <div className="flex justify-center">
    <button
      type="button"
      onClick={onClick}
      className={`${btnSecondary} px-5 py-3 text-sm`}
    >
      {children}
    </button>
  </div>
);
